/**
 * EmailKeyboardHandler — Gmail-compatible keyboard shortcuts.
 *
 * Replaces email_keyboard_controller.js.
 * Listens on document for keydown events and delegates to the emailStore
 * or direct DOM actions instead of going through Stimulus outlets.
 *
 * Shortcuts:
 *   j       → next email in list
 *   k       → previous email in list
 *   e       → archive current thread
 *   s       → star/unstar current thread
 *   r       → reply to current thread
 *   c       → compose new email
 *   /       → focus search input
 *   Escape  → blur search, or discard compose
 */

import { patch, get } from '@rails/request.js'
import { listContext } from './list-context.js'

export class EmailKeyboardHandler {
  constructor({ element, store }) {
    this._el    = element
    this._store = store
    this._onKeyDown = this._handleKeyDown.bind(this)
  }

  connect() {
    document.addEventListener('keydown', this._onKeyDown)
  }

  disconnect() {
    document.removeEventListener('keydown', this._onKeyDown)
  }

  // ── Main dispatcher ───────────────────────────────────────────────────────

  _handleKeyDown(e) {
    const key = e.key

    // Escape does NOT check isInputFocused — it intentionally blurs inputs
    if (key === 'Escape') {
      this._escape(e)
      return
    }

    if (this._isInputFocused()) return

    switch (key) {
      case 'j': e.preventDefault(); this._nextEmail(); break
      case 'k': e.preventDefault(); this._prevEmail(); break
      case 'e': e.preventDefault(); this._archive(); break
      case 's': e.preventDefault(); this._star(); break
      case 'r': e.preventDefault(); this._reply(); break
      case 'c': e.preventDefault(); this._compose(); break
      case '/': e.preventDefault(); this._focusSearch(); break
    }
  }

  // ── Shortcut actions ──────────────────────────────────────────────────────

  _nextEmail() {
    const listEl = this._el.querySelector('[data-controller~="email-list"]')
    if (!listEl) return
    const controller = this._getController(listEl, 'email-list')
    controller?.focusNext()
  }

  _prevEmail() {
    const listEl = this._el.querySelector('[data-controller~="email-list"]')
    if (!listEl) return
    const controller = this._getController(listEl, 'email-list')
    controller?.focusPrev()
  }

  _archive() {
    const threadId = this._store.currentThreadId.value
    if (!threadId) return
    patch(`/emails/threads/${threadId}/update_state`, {
      body: JSON.stringify({ action_type: 'archive', ...listContext() }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  _star() {
    const threadId = this._store.currentThreadId.value
    if (!threadId) return
    patch(`/emails/threads/${threadId}/update_state`, {
      body: JSON.stringify({ action_type: 'star', ...listContext() }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  _reply() {
    const threadId = this._store.currentThreadId.value
    if (!threadId) return
    get(`/emails/compose/reply/${threadId}`, { responseKind: 'turbo-stream' })
  }

  _compose() {
    const link = this._el.querySelector('[data-keyboard-compose]')
    if (link) link.click()
  }

  _focusSearch() {
    const input = this._el.querySelector("[data-email-search-target='input']")
    if (input) input.focus()
  }

  _escape(e) {
    const searchInput = this._el.querySelector("[data-email-search-target='input']")
    if (document.activeElement === searchInput) {
      searchInput.blur()
      return
    }
    // Discard active compose form
    const composePane = document.getElementById('email_compose_pane')
    if (composePane) {
      const composeEl = composePane.querySelector('[data-controller~="email-compose"]') || composePane
      const controller = this._getController(composeEl, 'email-compose')
      if (controller) {
        controller.discard({ preventDefault() {} })
      }
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _isInputFocused() {
    const el  = document.activeElement
    const tag = el?.tagName?.toLowerCase()
    return tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable
  }

  _getController(el, identifier) {
    // Access Stimulus application via the element's __stimulusApp__ or window.Stimulus
    const app = window.Stimulus || el?.__stimulusApp__
    if (!app) return null
    return app.getControllerForElementAndIdentifier(el, identifier)
  }
}
